import { defineStore } from 'pinia'
import type { GameRecord, GameCategoryRecord } from '~/types/datocms'

export interface GamesStoreState {
  allGames: GameRecord[]
  allGamesCount: number
  allCategories: GameCategoryRecord[]
  isLoading: boolean
  isLoadingMore: boolean
  itemsPerPage: number
  isButtonVisible: boolean
  skipItems: number
  filteredBy: GameCategoryRecord | null
}

export const useGamesStore = defineStore('GamesStore', {
  state: (): GamesStoreState => ({
    allGames: [],
    allGamesCount: 0,
    allCategories: [],
    isLoading: true,
    isLoadingMore: false,
    itemsPerPage: 10,
    isButtonVisible: false,
    skipItems: 0,
    filteredBy: null
  }),
  getters: {
    getCategoryBySlug(state) {
      return (catSlug: string) => state.allCategories.find((cat) => cat.slug === catSlug) || null
    }
  },
  actions: {
    async fetchGames(filter?: string) {
      this.setButtonVisibility(false)
      try {
        const { data } = await useGames({
          variables: {
            first: this.itemsPerPage,
            skip: this.skipItems,
            inCategory: filter as unknown as string[]
          }
        })

        this.allGamesCount = data._allGamesMeta.count

        const allGamesRes: GameRecord[] = data.allGames as GameRecord[]

        if (allGamesRes.length > 0) {
          this.allGames.push(...allGamesRes)
          this.skipItems += allGamesRes.length
        }
      } catch (error) {
        console.error(error)
      } finally {
        useTimeoutFn(() => {
          this.setLoading(false)
          this.setLoadingMore(false)
        }, 600)

        useTimeoutFn(() => {
          this.setButtonVisibility()
        }, 1000)
      }
    },
    pushCategories(payload: GamesStoreState['allCategories']) {
      this.allCategories = payload
    },
    pushFilter(category: GamesStoreState['filteredBy']) {
      this.filteredBy = category
    },
    setItemsPerPage(amount: number) {
      this.itemsPerPage = amount
    },
    setLoading(bool: boolean) {
      this.isLoading = bool
    },
    setLoadingMore(bool: boolean) {
      this.isLoadingMore = bool
    },
    setButtonVisibility(bool?: boolean) {
      this.isButtonVisible = bool ? bool : this.skipItems < this.allGamesCount
    },
    resetGames() {
      this.allGames = []
      this.allGamesCount = 0
      this.skipItems = 0
    }
  }
})
